import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTwin, Twin } from '../context/TwinContext';
import { usePlan, incrementTwinCount } from '../context/PlanContext';
import { twinApi } from '../services/apiClient';
import './ModelUpload.css';

// ── IndexedDB helpers for GLB storage ────────────────────────
const DB_NAME = 'twinmaker_models';
const STORE_NAME = 'glb_files';

export function openModelDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => { req.result.createObjectStore(STORE_NAME); };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export function storeModel(db: IDBDatabase, twinId: string, data: ArrayBuffer, fileName: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({ data, fileName }, twinId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export function loadModel(db: IDBDatabase, twinId: string): Promise<{ data: ArrayBuffer; fileName: string } | null> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(twinId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

// ── Types ──────────────────────────────────────────────────────
interface SceneNode {
  name: string;
  type: string; // 'Mesh' | 'Group' | 'Object3D' | etc
  meshCount: number;
  childCount: number;
  position: [number, number, number];
  children: SceneNode[];
  path: string; // e.g. "Root > Factory > Mixer_01"
}

interface MachineMapping {
  nodeName: string;
  machineName: string;
  machineType: string;
  sensors: string[];
  position: [number, number, number];
}

const MACHINE_TYPES = [
  'Robot Arm', 'Conveyor Belt', 'Pump', 'Mixer', 'Press',
  'Welding Gun', 'Vision System', 'Reactor', 'Turbine', 'Oven',
  'Compressor', 'Generator', 'Tank', 'Other'
];

const SENSOR_OPTIONS = [
  'Temperature', 'Pressure', 'Vibration', 'Speed/RPM', 'Humidity',
  'Flow Rate', 'Force/Torque', 'Current', 'Voltage', 'Weight',
  'Cycle Time', 'Proximity', 'Level', 'Power'
];

// Guess machine type from node name
const guessType = (name: string): string => {
  const n = name.toLowerCase();
  if (n.includes('robot') || n.includes('arm')) return 'Robot Arm';
  if (n.includes('conveyor') || n.includes('belt')) return 'Conveyor Belt';
  if (n.includes('pump')) return 'Pump';
  if (n.includes('mixer') || n.includes('blend')) return 'Mixer';
  if (n.includes('press')) return 'Press';
  if (n.includes('weld')) return 'Welding Gun';
  if (n.includes('camera') || n.includes('vision')) return 'Vision System';
  if (n.includes('reactor')) return 'Reactor';
  if (n.includes('turbine')) return 'Turbine';
  if (n.includes('oven') || n.includes('furnace')) return 'Oven';
  if (n.includes('compressor')) return 'Compressor';
  if (n.includes('generator')) return 'Generator';
  if (n.includes('tank')) return 'Tank';
  return 'Other';
};

// Default sensors based on machine type
const defaultSensors = (type: string): string[] => {
  switch (type) {
    case 'Robot Arm': return ['Temperature', 'Vibration', 'Force/Torque'];
    case 'Conveyor Belt': return ['Speed/RPM', 'Vibration', 'Temperature'];
    case 'Pump': return ['Pressure', 'Flow Rate', 'Vibration'];
    case 'Mixer': return ['Temperature', 'Speed/RPM', 'Vibration'];
    case 'Oven': return ['Temperature', 'Humidity', 'Power'];
    case 'Reactor': return ['Temperature', 'Pressure', 'Level'];
    case 'Turbine': return ['Speed/RPM', 'Vibration', 'Temperature'];
    case 'Generator': return ['Voltage', 'Current', 'Temperature'];
    case 'Tank': return ['Level', 'Pressure', 'Temperature'];
    default: return ['Temperature', 'Pressure'];
  }
};

// ── Parse GLB scene graph ──────────────────────────────────────
// Strategy: walk the full tree, make ALL mesh-containing nodes selectable.
// Auto-detect "machine-level" nodes using a scoring heuristic.
const SKIP_NAMES = /^(Scene|Root|unnamed|node_\d|RootNode)$/i;

const parseSceneGraph = async (file: File): Promise<{ nodes: SceneNode[]; allNodes: SceneNode[]; machineNodes: SceneNode[] }> => {
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');

  return new Promise((resolve, reject) => {
    const loader = new GLTFLoader();
    const url = URL.createObjectURL(file);

    loader.load(url, (gltf) => {
      const allNodes: SceneNode[] = [];

      const traverse = (obj: any, parentPath: string, depth: number): SceneNode => {
        const path = parentPath ? `${parentPath} > ${obj.name || 'unnamed'}` : (obj.name || 'Root');
        const meshCount = obj.isMesh ? 1 : 0;
        const children = (obj.children || []).map((c: any) => traverse(c, path, depth + 1));
        const totalMeshes = meshCount + children.reduce((s: number, c: SceneNode) => s + c.meshCount, 0);

        const node: SceneNode = {
          name: obj.name || `node_${allNodes.length}`,
          type: obj.type || 'Object3D',
          meshCount: totalMeshes,
          childCount: children.length,
          position: [
            +(obj.position?.x || 0).toFixed(2),
            +(obj.position?.y || 0).toFixed(2),
            +(obj.position?.z || 0).toFixed(2),
          ],
          children,
          path,
        };
        allNodes.push(node);
        return node;
      };

      const rootNodes = gltf.scene.children.map((c: any) => traverse(c, '', 1));
      URL.revokeObjectURL(url);

      // Smart auto-detection: score each node
      const candidates = allNodes.filter(n => n.meshCount > 0 && !SKIP_NAMES.test(n.name));

      // If very few nodes (≤5 with meshes), select them all
      if (candidates.length <= 5) {
        resolve({ nodes: rootNodes, allNodes, machineNodes: candidates });
        return;
      }

      // Otherwise pick the "best" level: prefer groups over raw meshes,
      // prefer nodes with children (assemblies) over leaf meshes
      const scored = candidates.map(n => {
        let score = 0;
        if (n.childCount > 0) score += 3;        // groups/assemblies
        if (n.meshCount > 1) score += 2;          // multi-mesh = assembly
        if (!n.name.match(/\d{3,}/)) score += 1;  // not auto-generated names
        if (n.type === 'Group' || n.type === 'Object3D') score += 2;
        if (n.type === 'Mesh' && n.childCount === 0) score -= 2; // leaf mesh = part
        return { node: n, score };
      }).sort((a, b) => b.score - a.score);

      // Take top-scoring nodes, but deduplicate parent/child overlaps
      const selected: SceneNode[] = [];
      const selectedPaths = new Set<string>();

      for (const { node } of scored) {
        // Skip if an ancestor is already selected
        const isChild = [...selectedPaths].some(p => node.path.startsWith(p + ' > '));
        if (isChild) continue;
        // Skip if a descendant is already selected — remove the descendant, keep this parent
        const descendants = [...selectedPaths].filter(p => p.startsWith(node.path + ' > '));
        descendants.forEach(p => {
          selectedPaths.delete(p);
          const idx = selected.findIndex(s => s.path === p);
          if (idx >= 0) selected.splice(idx, 1);
        });
        selected.push(node);
        selectedPaths.add(node.path);
      }

      resolve({ nodes: rootNodes, allNodes, machineNodes: selected });
    }, undefined, (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    });
  });
};

// ── Component ──────────────────────────────────────────────────
const ModelUpload: React.FC = () => {
  const navigate = useNavigate();
  const { createTwin } = useTwin();
  const { checkTwinCreation, plan, nextPlan } = usePlan();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState(1); // 1=upload, 2=tree, 3=mapping, 4=sensors, 5=confirm
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [sceneNodes, setSceneNodes] = useState<SceneNode[]>([]);
  const [allNodes, setAllNodes] = useState<SceneNode[]>([]);
  const [selectedNodes, setSelectedNodes] = useState<Set<string>>(new Set());
  const [mappings, setMappings] = useState<MachineMapping[]>([]);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // ── Step 1: File upload ──────────────────────────────────────
  const handleFile = useCallback(async (f: File) => {
    if (!f.name.match(/\.(glb|gltf)$/i)) {
      setError('Please upload a .glb or .gltf file');
      return;
    }
    setFile(f);
    setError('');
    setParsing(true);
    try {
      const { nodes, allNodes: all, machineNodes } = await parseSceneGraph(f);
      setSceneNodes(nodes);
      setAllNodes(all);
      // Auto-select only the detected machine-level nodes
      const autoSelect = new Set<string>(machineNodes.map(n => n.name));
      setSelectedNodes(autoSelect);
      setStep(2);
    } catch (e: any) {
      setError(`Failed to parse model: ${e.message}`);
    } finally {
      setParsing(false);
    }
  }, []);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  }, [handleFile]);

  // ── Step 2 → 3: Build mappings from selected nodes ──────────
  const buildMappings = () => {
    const maps: MachineMapping[] = [];
    allNodes.forEach(n => {
      if (selectedNodes.has(n.name)) {
        const type = guessType(n.name);
        maps.push({
          nodeName: n.name,
          machineName: n.name.replace(/[_-]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          machineType: type,
          sensors: defaultSensors(type),
          position: n.position,
        });
      }
    });
    setMappings(maps);
    setStep(3);
  };

  // ── Step 5: Create twin ──────────────────────────────────────
  const handleCreate = async () => {
    // Check twin creation limit
    const limitCheck = checkTwinCreation();
    if (!limitCheck.allowed) {
      setError(limitCheck.reason || 'Twin limit reached. Please upgrade your plan.');
      return;
    }
    if (!companyName.trim()) { setError('Company name is required'); return; }
    setLoading(true);
    setError('');
    try {
      const twinId = `twin-${Date.now()}`;
      const machines = mappings.map((m, idx) => ({
        id: `m-${idx}`,
        name: m.machineName,
        type: m.machineType,
        sensors: m.sensors,
        nodeName: m.nodeName,
        position: m.position,
      }));

      // Store GLB in IndexedDB
      let modelUrl: string | undefined;
      if (file) {
        try {
          const arrayBuf = await file.arrayBuffer();
          const db = await openModelDB();
          await storeModel(db, twinId, arrayBuf, file.name);
          modelUrl = `indexeddb://${twinId}`;
        } catch { /* continue without model storage */ }
      }

      const newTwin: Twin = {
        id: twinId,
        name: companyName,
        company: companyName,
        factoryType: 'custom',
        machines: machines.map((m) => ({
          machineId: m.id,
          name: m.name,
          type: m.type,
          status: 'running' as const,
          sensors: m.sensors.map((sName, sidx) => ({
            sensorId: `${m.id}-s${sidx}`,
            name: sName,
            type: sName.toLowerCase().replace(/[/ ]/g, '_'),
            value: 85,
            min: 0,
            max: 100,
            unit: sName.includes('Temp') ? '°C' : sName.includes('Speed') ? 'RPM' : sName.includes('Pressure') ? 'bar' : '%',
          })),
        })),
        simulatedMachineData: {},
        anomalies: [],
        healthScore: 100,
        lastUpdated: new Date(),
        createdAt: new Date(),
        modelUrl,
      };

      // Create twin locally first (always works)
      createTwin(newTwin);
      incrementTwinCount();

      // Optionally sync to backend (don't block on failure)
      twinApi.create({
        company: companyName,
        factoryType: 'custom',
        machines,
        sensors: [...new Set(mappings.flatMap(m => m.sensors))],
      }).catch(() => {});

      // Go straight to dashboard
      navigate('/app');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Toggle node selection ────────────────────────────────────
  const toggleNode = (name: string) => {
    setSelectedNodes(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // ── Render node tree ─────────────────────────────────────────
  const renderTree = (nodes: SceneNode[], depth = 0): React.ReactNode => {
    return nodes.map(n => (
      <div key={n.path} style={{ marginLeft: depth * 20 }}>
        <div
          className={`tree-node ${selectedNodes.has(n.name) ? 'selected' : ''} ${n.meshCount > 0 ? 'has-mesh' : ''}`}
          onClick={() => n.meshCount > 0 && toggleNode(n.name)}
        >
          <span className="tn-expand">{n.children.length > 0 ? '▸' : '·'}</span>
          <input
            type="checkbox"
            checked={selectedNodes.has(n.name)}
            onChange={(e) => { e.stopPropagation(); toggleNode(n.name); }}
            onClick={(e) => e.stopPropagation()}
          />
          <span className="tn-icon">{n.meshCount > 0 ? '🔧' : '📁'}</span>
          <span className="tn-name">{n.name}</span>
          <span className="tn-type">{n.type}</span>
          <span className="tn-meta">
            {n.meshCount > 0 && <span className="tn-badge mesh">{n.meshCount} mesh{n.meshCount > 1 ? 'es' : ''}</span>}
            {n.childCount > 0 && <span className="tn-badge child">{n.childCount} children</span>}
          </span>
        </div>
        {n.children.length > 0 && renderTree(n.children, depth + 1)}
      </div>
    ));
  };

  const stepLabels = ['Upload', 'Select Nodes', 'Map Machines', 'Sensors', 'Create'];

  return (
    <div className="model-upload-page">
      <div className="model-upload-card">
      <div className="onboarding-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
        🏭 TwinMaker<span>SaaS</span>
      </div>

        {/* Progress */}
        <div className="onboarding-steps">
          {stepLabels.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`ob-step ${step >= i + 1 ? 'active' : ''} ${step > i + 1 ? 'done' : ''}`}>
                <div className="ob-step-dot">{step > i + 1 ? '✓' : i + 1}</div>
                <span>{s}</span>
              </div>
              {i < 4 && <div className={`ob-step-line ${step > i + 1 ? 'done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1: Upload ──────────────────────────────────── */}
        {step === 1 && (
          <div className="onboarding-step">
            <h2>Upload Your 3D Model</h2>
            <p className="ob-subtitle">
              Drop a GLB/glTF file — we'll parse the scene graph and auto-detect machines
            </p>

            <div
              className={`drop-zone ${dragOver ? 'drag-over' : ''} ${file ? 'has-file' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".glb,.gltf"
                style={{ display: 'none' }}
                onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
              />
              {parsing ? (
                <>
                  <div className="dz-icon spin">⚙️</div>
                  <p className="dz-title">Parsing model…</p>
                  <p className="dz-hint">Extracting scene graph from {file?.name}</p>
                </>
              ) : file ? (
                <>
                  <div className="dz-icon">✅</div>
                  <p className="dz-title">{file.name}</p>
                  <p className="dz-hint">{(file.size / 1024 / 1024).toFixed(1)} MB — Click to change</p>
                </>
              ) : (
                <>
                  <div className="dz-icon">📦</div>
                  <p className="dz-title">Drop GLB / glTF file here</p>
                  <p className="dz-hint">or click to browse · Supports .glb and .gltf</p>
                </>
              )}
            </div>

            <div className="upload-tips">
              <h4>💡 Tips for best results</h4>
              <ul>
                <li>Name your model nodes clearly (e.g. <code>Mixer_01</code>, <code>Conveyor_A</code>)</li>
                <li>Group related parts under parent nodes</li>
                <li>Export from Blender, SolidWorks, Revit, or any CAD tool as glTF/GLB</li>
                <li>Models under 50MB work best in the browser</li>
              </ul>
            </div>

            {error && <div className="error-msg">⚠️ {error}</div>}
          </div>
        )}

        {/* ── Step 2: Scene Tree ──────────────────────────────── */}
        {step === 2 && (
          <div className="onboarding-step config-step">
            <h2>Select Machine Nodes</h2>
            <p className="ob-subtitle">
              We auto-detected {selectedNodes.size} machine-level nodes.
              Uncheck parts/sub-components you don't want as separate machines.
            </p>

            <div className="tree-stats">
              <span className="ts-item">📦 {allNodes.length} total nodes</span>
              <span className="ts-item">🔧 {allNodes.filter(n => n.meshCount > 0).length} with meshes</span>
              <span className="ts-item selected">✅ {selectedNodes.size} selected</span>
            </div>

            <div className="scene-tree">
              {renderTree(sceneNodes)}
            </div>

            {error && <div className="error-msg">⚠️ {error}</div>}

            <div className="onboarding-actions">
              <button className="btn-secondary" onClick={() => { setStep(1); setFile(null); setSceneNodes([]); }}>
                ← Change File
              </button>
              <button
                className="btn-primary"
                onClick={() => {
                  if (selectedNodes.size === 0) { setError('Select at least one node'); return; }
                  setError('');
                  buildMappings();
                }}
                disabled={selectedNodes.size === 0}
              >
                Map {selectedNodes.size} Node{selectedNodes.size !== 1 ? 's' : ''} →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Machine Mapping ─────────────────────────── */}
        {step === 3 && (
          <div className="onboarding-step config-step">
            <h2>Map Nodes to Machines</h2>
            <p className="ob-subtitle">Confirm or edit the machine name and type for each node</p>

            <div className="machines-config">
              {mappings.map((m, idx) => (
                <div key={m.nodeName} className="machine-config-card">
                  <div className="machine-config-header">
                    <h4>
                      <span className="mch-node-badge">{m.nodeName}</span>
                    </h4>
                    <button
                      className="btn-remove"
                      onClick={() => setMappings(mappings.filter((_, i) => i !== idx))}
                    >✕</button>
                  </div>

                  <div className="form-group">
                    <label>Machine Name</label>
                    <input
                      className="input"
                      value={m.machineName}
                      onChange={e => {
                        const u = [...mappings];
                        u[idx].machineName = e.target.value;
                        setMappings(u);
                      }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Machine Type</label>
                    <select
                      className="input"
                      value={m.machineType}
                      onChange={e => {
                        const u = [...mappings];
                        u[idx].machineType = e.target.value;
                        u[idx].sensors = defaultSensors(e.target.value);
                        setMappings(u);
                      }}
                    >
                      {MACHINE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>

                  <div className="mapping-pos">
                    📍 Position: ({m.position.join(', ')})
                  </div>
                </div>
              ))}
            </div>

            <div className="onboarding-actions">
              <button className="btn-secondary" onClick={() => setStep(2)}>← Back</button>
              <button
                className="btn-primary"
                onClick={() => { if (mappings.length === 0) { setError('Add at least one machine'); return; } setError(''); setStep(4); }}
                disabled={mappings.length === 0}
              >
                Configure Sensors →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 4: Sensors ─────────────────────────────────── */}
        {step === 4 && (
          <div className="onboarding-step config-step">
            <h2>Configure Sensors</h2>
            <p className="ob-subtitle">Select which sensors to attach to each machine</p>

            <div className="machines-config">
              {mappings.map((m, idx) => (
                <div key={m.nodeName} className="machine-config-card">
                  <div className="machine-config-header">
                    <h4>{m.machineName} <span className="mch-type-badge">{m.machineType}</span></h4>
                  </div>
                  <div className="sensors-checkboxes">
                    {SENSOR_OPTIONS.map(s => (
                      <label key={s} className="checkbox-inline">
                        <input
                          type="checkbox"
                          checked={m.sensors.includes(s)}
                          onChange={e => {
                            const u = [...mappings];
                            if (e.target.checked) u[idx].sensors.push(s);
                            else u[idx].sensors = u[idx].sensors.filter(x => x !== s);
                            setMappings(u);
                          }}
                        />
                        <span>{s}</span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="onboarding-actions">
              <button className="btn-secondary" onClick={() => setStep(3)}>← Back</button>
              <button className="btn-primary" onClick={() => setStep(5)}>Review & Create →</button>
            </div>
          </div>
        )}

        {/* ── Step 5: Confirm ─────────────────────────────────── */}
        {step === 5 && (
          <div className="onboarding-step">
            <h2>Review & Create Twin</h2>
            <p className="ob-subtitle">Confirm your configuration before creating</p>

            <div className="form-field">
              <label>Company / Twin Name</label>
              <input
                className="input"
                placeholder="e.g. ACME Manufacturing"
                value={companyName}
                onChange={e => setCompanyName(e.target.value)}
              />
            </div>

            <div className="summary-box">
              <div className="summary-item">
                <span className="summary-label">3D Model</span>
                <span className="summary-value">{file?.name} ({(file?.size || 0 / 1024 / 1024).toFixed(1)} MB)</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Machines ({mappings.length})</span>
                <div className="machines-summary">
                  {mappings.map(m => (
                    <div key={m.nodeName} className="machine-summary-item">
                      <span className="machine-name">{m.machineName}</span>
                      <span className="machine-type">{m.machineType}</span>
                      <span className="machine-sensors">{m.sensors.length} sensors</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Sensors</span>
                <span className="summary-value">{mappings.reduce((s, m) => s + m.sensors.length, 0)}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Source</span>
                <span className="summary-value">3D Model Import</span>
              </div>
            </div>

            {error && <div className="error-msg">⚠️ {error}</div>}

            <div className="onboarding-actions">
              <button className="btn-secondary" onClick={() => setStep(4)}>← Back</button>
              <button className="btn-primary" onClick={handleCreate} disabled={loading || !companyName.trim()}>
                {loading ? '⏳ Creating Twin…' : '✨ Create Twin from Model'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ModelUpload;
